import React from 'react';
import { useQuery } from '@tanstack/react-query';
import portfolioService, { RiskData, ValuationData } from '../../services/portfolio';
import LoadingSpinner from '../ui/LoadingSpinner';
import RiskCharts from './RiskCharts';
import ValuationSection from './ValuationSection';
import RiskSection from './RiskSection';
import ReturnCorrelationCard from './ReturnCorrelationCard';

interface Props {
  portfolioId: number;
}

// Single entry point for the Analytics tab. It owns the two underlying queries
// (valuation + risk) so the whole tab shows ONE spinner until both resolve,
// instead of each card flashing its own. The child components below reuse the
// same query keys, so react-query serves them from cache with no extra fetch
// and no inner spinners.
const AnalyticsTab: React.FC<Props> = ({ portfolioId }) => {
  const valuation = useQuery<ValuationData>({
    queryKey: ['portfolio-valuation', portfolioId],
    queryFn: () => portfolioService.getPortfolioValuation(portfolioId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const risk = useQuery<RiskData>({
    queryKey: ['portfolio-risk', portfolioId],
    queryFn: () => portfolioService.getPortfolioRisk(portfolioId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // isLoading is true only on first load with no cached data, so revisits
  // within the stale window render instantly without the gate.
  if (valuation.isLoading || risk.isLoading) {
    return (
      <div className="card card-forest">
        <div className="card-body d-flex align-items-center justify-content-center py-5">
          <LoadingSpinner text="Loading analytics…" />
        </div>
      </div>
    );
  }

  return (
    <>
      <RiskCharts portfolioId={portfolioId} />
      <ValuationSection
        portfolioId={portfolioId}
        riskRow={
          <div className="row g-4">
            <div className="col-12 col-lg-6">
              <RiskSection portfolioId={portfolioId} />
            </div>
            <div className="col-12 col-lg-6">
              <ReturnCorrelationCard portfolioId={portfolioId} />
            </div>
          </div>
        }
      />
    </>
  );
};

export default AnalyticsTab;
